import { useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeMessage } from '@ridemaadi/types';
import { useAuth } from '../../providers/AuthProvider';
import { useMyRides } from './hooks';
import { locationApi } from './location';
import { rideKeys } from './hooks';
import { rideNotificationService } from './rideNotificationService';
import type { RideStartedBannerPayload } from './RideStartedBanner';

const MAX_SOCKETS = 5;

function handleRideStarted(
  msg: Extract<RealtimeMessage, { type: 'ride_started' }>,
  onBanner: (payload: RideStartedBannerPayload) => void,
) {
  void rideNotificationService.notifyRideStarted(msg.rideId, msg.rideTitle, msg.startedByName);
  if (document.visibilityState === 'visible') {
    onBanner({
      rideId: msg.rideId,
      rideTitle: msg.rideTitle,
      startedByName: msg.startedByName,
    });
  }
}

/** Subscribe to realtime for joined scheduled rides to deliver start notifications. */
export function RideStartNotificationListener({
  onRideStarted,
}: {
  onRideStarted: (payload: RideStartedBannerPayload) => void;
}) {
  const { user } = useAuth();
  const { data } = useMyRides({ enabled: Boolean(user) });
  const qc = useQueryClient();
  const onBannerRef = useRef(onRideStarted);
  onBannerRef.current = onRideStarted;

  const trackedRideIds = useMemo(() => {
    const ids =
      data?.items
        .filter((r) => r.status === 'scheduled' || r.status === 'active')
        .slice(0, MAX_SOCKETS)
        .map((r) => r.id) ?? [];
    return ids.join(',');
  }, [data?.items]);

  useEffect(() => {
    if (!user || !trackedRideIds) return;

    const rideIds = trackedRideIds.split(',');
    let closed = false;
    const sockets: WebSocket[] = [];

    const connectRide = async (rideId: string) => {
      try {
        const { url } = await locationApi.realtimeUrl(rideId);
        if (closed) return;
        const ws = new WebSocket(url);
        sockets.push(ws);

        ws.onmessage = (event) => {
          const msg = JSON.parse(event.data as string) as RealtimeMessage;
          if (msg.type === 'ride_started') {
            handleRideStarted(msg, (p) => onBannerRef.current(p));
            qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
            qc.invalidateQueries({ queryKey: rideKeys.mine });
          }
          if (msg.type === 'status_change' && msg.status === 'active') {
            qc.invalidateQueries({ queryKey: rideKeys.detail(rideId) });
            qc.invalidateQueries({ queryKey: rideKeys.mine });
            if (msg.rideId && msg.rideTitle && msg.startedByName) {
              void rideNotificationService.notifyRideStarted(msg.rideId, msg.rideTitle, msg.startedByName);
              if (document.visibilityState === 'visible') {
                onBannerRef.current({
                  rideId: msg.rideId,
                  rideTitle: msg.rideTitle,
                  startedByName: msg.startedByName,
                });
              }
            }
          }
        };
      } catch {
        /* polling fallback via react-query */
      }
    };

    rideIds.forEach((id) => void connectRide(id));

    return () => {
      closed = true;
      sockets.forEach((ws) => ws.close());
    };
  }, [user, trackedRideIds, qc]);

  return null;
}
